import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import Header from '../components/Header'

export default function Referrals() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [referrals, setReferrals] = useState([])
  const [loadingRefs, setLoadingRefs] = useState(true)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!user) return // ProtectedRoute sends logged-out visitors to sign-in
    supabase.from('profiles').update({ last_seen_referrals_at: new Date().toISOString() }).eq('id', user.id)
    const load = async () => {
      let { data } = await supabase
        .from('referrals')
        .select('id, status, created_at, qualified_at, referred_id')
        .eq('referrer_id', user.id)
        .order('created_at', { ascending: false })
      const _rids = [...new Set((data || []).map(r => r.referred_id).filter(Boolean))]
      let _rmap = {}
      if (_rids.length) {
        const { data: _rp } = await supabase.from('public_profiles').select('id, full_name').in('id', _rids)
        ;(_rp || []).forEach(x => { _rmap[x.id] = x })
      }
      data = (data || []).map(r => ({ ...r, referred: _rmap[r.referred_id] || null }))
      setReferrals(data || [])
      setLoadingRefs(false)
    }
    load()
  }, [user, profile])

  if (!user || !profile) {
    return <div><Header /><div className="spinner-wrap"><div className="spinner" /></div></div>
  }

  const referralLink = profile.referral_code
    ? `https://hireitnow.au/auth?mode=signup&ref=${profile.referral_code}`
    : null
  const credits = profile.referral_fee_credits || 0
  const qualifiedCount = referrals.filter(r => r.status === 'qualified').length

  const copyLink = () => {
    if (!referralLink) return
    navigator.clipboard.writeText(referralLink)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div>
      <Header />
      <div className="container section" style={{ maxWidth: 640 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>← Back</button>
        <h1 style={{ marginBottom: 8 }}>🎁 Refer a friend</h1>
        <p style={{ color: 'var(--text-muted, #666)', marginTop: 0 }}>
          Share your link. When someone you refer signs up and completes their first hire,
          your next 3 hires get a reduced platform fee.
        </p>

        <div style={{ background: 'var(--primary-pale, #f4f4f4)', borderRadius: 12, padding: 20, margin: '20px 0' }}>
          <div style={{ fontSize: 14, color: 'var(--text-muted, #666)', marginBottom: 4 }}>Discounted hires available</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>{credits}</div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Your referral link</div>
          {referralLink ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input readOnly value={referralLink}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border, #ddd)', fontSize: 13 }} />
              <button onClick={copyLink}
                style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: 'white', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          ) : (
            <div style={{ color: 'var(--text-muted, #666)', fontSize: 14 }}>Generating your link…</div>
          )}
        </div>

        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
            People you've referred {referrals.length > 0 && `(${qualifiedCount} qualified)`}
          </div>
          {loadingRefs ? (
            <div style={{ color: 'var(--text-muted, #666)', fontSize: 14 }}>Loading…</div>
          ) : referrals.length === 0 ? (
            <div style={{ color: 'var(--text-muted, #666)', fontSize: 14 }}>
              No referrals yet. Share your link to get started.
            </div>
          ) : (
            referrals.map(r => (
              <div key={r.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--border, #eee)' }}>
                <span>{r.referred?.full_name || 'New member'}</span>
                <span style={{
                  fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                  background: r.status === 'qualified' ? 'var(--green, #1a7f37)' : 'var(--border, #eee)',
                  color: r.status === 'qualified' ? 'white' : 'var(--text-muted, #666)'
                }}>
                  {r.status === 'qualified' ? 'Earned ✓' : 'Signed up'}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
